import { useEffect, useState } from "react";
import useAxios from "../../utils/useAxios";
import { FaSpinner } from "react-icons/fa";
import { hideModal, showModal } from "../../utils/modalUtility";
import Pagination from "../common/pagination";
import Loader from "../common/Loader";

const INITIAL_FORM_DATA = {
  fullName: "",
  phoneNumber: "",
  alternateMobile: "",
  email: "",
  address: "",
  profession: "",
  message: "",
  remark: "",
  status: "",
  propertyName: "",
  propertyInterested: "",
  budgetMin: "",
  budgetMax: "",
  followUpDateTime: "",
};

function Enquiries() {
  const [properties, setProperties] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [statusList, setStatusList] = useState([]);
  const [notFoundMessage, setNotFoundMessage] = useState("");
  const axiosAPI = useAxios();

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [formData, setFormData] = useState(INITIAL_FORM_DATA);

  const removeFormData = () => setFormData(INITIAL_FORM_DATA);

  const [filters, setFilters] = useState({
    status: "",
    from_date: "",
    to_date: "",
  });

  // Handler to update filters
  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // console.log("{Properties",properties)

  const buildQuery = (filterObj) => {
    const params = new URLSearchParams();
    Object.entries(filterObj).forEach(([key, value]) => {
      if (value) params.append(key, value);
    });
    params.append("page", page);
    return params.toString();
  };

  const getProperty = async () => {
    try {
      const response = await axiosAPI.get("/api/properties/get-properties");
      setProperties(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  const getEnquiries = async () => {
    try {
      const queryString = buildQuery(filters);
      setLoading(true);
      const response = await axiosAPI.get(
        `/api/enquiries/get-enquiries?${queryString}`,
      );
      const { results, totalPages, success } = response.data;
      if (success && Array.isArray(results)) {
        setEnquiries(results);
        setTotalPages(totalPages);
        setNotFoundMessage(results.length === 0 ? "No enquiries found." : "");
      } else {
        setEnquiries([]);
        setNotFoundMessage("No enquiries found.");
      }
    } catch (err) {
      console.error("Error fetching enquiries:", err);
      setEnquiries([]);
      setNotFoundMessage(
        err.response?.status === 404
          ? "No enquiries found."
          : "Something went wrong. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const getStatuses = async () => {
    try {
      const response = await axiosAPI.get("/api/enquiries/get-statusList");
      setStatusList(response.data.result);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    getProperty();
    getStatuses();
  }, []);

  useEffect(() => {
    getEnquiries();
  }, [page]);

  const formatDateTimeForInput = (value) => {
    if (!value) return "";

    const parsedDate = new Date(value);
    if (Number.isNaN(parsedDate.getTime())) return "";

    const year = parsedDate.getFullYear();
    const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
    const day = String(parsedDate.getDate()).padStart(2, "0");
    const hours = String(parsedDate.getHours()).padStart(2, "0");
    const minutes = String(parsedDate.getMinutes()).padStart(2, "0");

    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.id]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      full_name: formData.fullName,
      phone_number: formData.phoneNumber,
      email: formData.email,
      address: formData.address,
      selected_property: formData.propertyName,
      status: formData.status,
      message: formData.message,
      follow_up_datetime: formData.followUpDateTime,
      alternate_mobile: formData.alternateMobile,
      profession: formData.profession,
      property_interested: formData.propertyInterested,
      budget_min: formData.budgetMin,
      budget_max: formData.budgetMax,
      remark: formData.remark,
    };

    // console.log("Submitting form with payload:", payload);
    // setLoading(false)
    // return

    setIsLoading(true);
    try {
      if (isEditMode) {
        await axiosAPI.put(
          `/api/enquiries/update-enquiry/${currentUserId}`,
          payload,
        );
        window.notifyMessage(
          "Success",
          "Enquiry updated successfully",
          "success",
        );
      } else {
        await axiosAPI.post("/api/enquiries/add-enquiry", payload);
        window.notifyMessage(
          "Success",
          "Enquiry added successfully",
          "success",
        );
      }
      hideModal("newUserModal");
      getEnquiries();
      closeModal();
    } catch (error) {
      const msg = error.response?.data?.message;
      window.notifyMessage("Error", msg || "There was an error!", "danger");
    } finally {
      setIsLoading(false);
    }
  };

  const openEditModal = (id) => {
    setIsEditMode(true);
    setCurrentUserId(id);
    const selectedProperty = enquiries.find((enq) => enq.id === id);
    // console.log("Selected enquiry for editing:", selectedProperty);
    if (selectedProperty) {
      setFormData({
        fullName: selectedProperty.full_name,
        phoneNumber: selectedProperty.phone_number,
        alternateMobile: selectedProperty.alternate_mobile || "",
        email: selectedProperty.email,
        address: selectedProperty.address,
        profession: selectedProperty.profession || "",
        message: selectedProperty.message,
        remark: selectedProperty.remark || "",
        status: selectedProperty.status,
        propertyName: selectedProperty.selected_property,
        propertyInterested: selectedProperty.property_interested || "",
        budgetMin: selectedProperty.budget_min || "",
        budgetMax: selectedProperty.budget_max || "",
        followUpDateTime: formatDateTimeForInput(
          selectedProperty.follow_up_datetime,
        ),
      });
    }

    const modal = new window.bootstrap.Modal(
      document.getElementById("newUserModal"),
    );
    modal.show();
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this?")) return;
    try {
      await axiosAPI.delete(`/api/enquiries/delete-enquiry/${id}`);
      window.notifyMessage("Success", "Deleted successfully", "success");
      getEnquiries();
    } catch (error) {
      console.error("Error deleting:", error);
      window.notifyMessage(
        "Error",
        "An error occurred while deleting.",
        "danger",
      );
    }
  };

  // Close Modal
  const closeModal = () => {
    setIsEditMode(false);
    removeFormData();
    setCurrentUserId(null);
  };

  const openAddModal = () => {
    showModal("newUserModal");
  };

  return (
    <div className="container">
      <div className="page-inner">
        <div className="d-flex justify-content-end align-items-end flex-column flex-md-row">
          <div>
            <button
              type="button"
              className="btn btn-dark btn-round mb-4"
              onClick={() => openAddModal()}
            >
              Add Enquiry
            </button>
          </div>
        </div>

        {/* Modal */}
        <div
          className="modal fade"
          id="newUserModal"
          tabIndex="-1"
          aria-labelledby="newUserModalLabel"
          aria-hidden="true"
        >
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="newUserModalLabel">
                  {isEditMode ? "Edit Enquiry" : "Add Enquiry"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                  onClick={closeModal}
                ></button>
              </div>

              <div className="modal-body">
                <form onSubmit={handleSubmit}>
                  <div className="row g-3">
                    {/* Full Name | Phone Number */}
                    <div className="col-md-6 col-sm-12">
                      <label htmlFor="fullName" className="form-label">
                        Full Name
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="fullName"
                        placeholder="Enter Your Name"
                        value={formData.fullName}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="col-md-6 col-sm-12">
                      <label htmlFor="phoneNumber" className="form-label">
                        Phone Number
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="phoneNumber"
                        placeholder="Enter Phone Number"
                        value={formData.phoneNumber}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    {/* Alternate Mobile | Email */}
                    <div className="col-md-6 col-sm-12">
                      <label htmlFor="alternateMobile" className="form-label">
                        Alternate Mobile{" "}
                        <span className="text-muted">(Optional)</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="alternateMobile"
                        placeholder="Enter Alternate Mobile"
                        value={formData.alternateMobile}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="col-md-6 col-sm-12">
                      <label htmlFor="email" className="form-label">
                        Email
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="email"
                        placeholder="Enter Email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    {/* Address | Profession */}
                    <div className="col-md-6 col-sm-12">
                      <label htmlFor="address" className="form-label">
                        Address
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="address"
                        placeholder="Enter Address"
                        value={formData.address}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="col-md-6 col-sm-12">
                      <label htmlFor="profession" className="form-label">
                        Profession{" "}
                        <span className="text-muted">(Optional)</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="profession"
                        placeholder="Enter Profession"
                        value={formData.profession}
                        onChange={handleChange}
                      />
                    </div>

                    {/* Message | Follow-up Date & Time */}
                    <div className="col-md-6 col-sm-12">
                      <label htmlFor="message" className="form-label">
                        Message
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="message"
                        placeholder="Enter Message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="col-md-6 col-sm-12">
                      <label htmlFor="followUpDateTime" className="form-label">
                        Follow-up Date &amp; Time
                      </label>
                      <input
                        type="datetime-local"
                        className="form-control"
                        id="followUpDateTime"
                        value={formData.followUpDateTime}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    {/* Select Property | Property Interested */}
                    <div className="col-md-6 col-sm-12">
                      <label htmlFor="propertyName" className="form-label">
                        Select Property
                      </label>
                      <select
                        className="form-select"
                        id="propertyName"
                        name="propertyName"
                        required
                        value={formData.propertyName}
                        onChange={handleChange}
                      >
                        <option value="" disabled>
                          Select Property Type
                        </option>
                        {properties.map((option) => (
                          <option key={option.id} value={option.id}>
                            {option.property_name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Budget */}
                    <div className="col-md-6 col-sm-12">
                      <label className="form-label">Budget</label>
                      <div className="d-flex gap-2">
                        <input
                          type="number"
                          className="form-control"
                          id="budgetMin"
                          placeholder="Min"
                          value={formData.budgetMin}
                          onChange={handleChange}
                          min="0"
                        />
                        <input
                          type="number"
                          className="form-control"
                          id="budgetMax"
                          placeholder="Max"
                          value={formData.budgetMax}
                          onChange={handleChange}
                          min="0"
                        />
                      </div>
                    </div>

                    {/* Select Status | (full width) */}
                    <div className="col-md-6 col-sm-12">
                      <label htmlFor="status" className="form-label">
                        Select Status
                      </label>
                      <select
                        className="form-select"
                        id="status"
                        name="status"
                        required
                        value={formData.status}
                        onChange={handleChange}
                      >
                        <option value="" disabled>
                          Select Status Type
                        </option>
                        {statusList?.length > 0 &&
                          statusList.map((item) => (
                            <option key={item?.id} value={item?.value}>
                              {item?.status}
                            </option>
                          ))}
                      </select>
                    </div>

                    {/* Remark — full width */}
                    <div className="col-12">
                      <label htmlFor="remark" className="form-label">
                        Remark
                      </label>
                      <textarea
                        className="form-control"
                        id="remark"
                        placeholder="Enter Remark"
                        rows="3"
                        value={formData.remark}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                  <button
                    type="submit"
                    className="btn btn-primary btn-round btn-sm"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <FaSpinner className="spinner" />
                    ) : isEditMode ? (
                      "Update Enquiry"
                    ) : (
                      "Add Enquiry"
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>

        {/* Notification Table */}
        <div className="row">
          <div className="col-md-12">
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-3">
                {/* Left Section - User Details */}
                <div className="d-flex align-items-center">
                  <h4 className="card-title mb-0">
                    All Enquiries ({enquiries.length})
                  </h4>
                </div>

                {/* Right Section - Filters */}
                <div className="d-flex gap-3 flex-wrap ">
                  {/* Status Dropdown */}
                  <div
                    className="d-flex flex-column"
                    style={{ minWidth: "180px" }}
                  >
                    <label className="form-label mb-1">Status</label>
                    <select
                      className="form-select"
                      value={filters.status}
                      onChange={(e) =>
                        handleFilterChange("status", e.target.value)
                      }
                      style={{ width: "100%" }}
                    >
                      <option value="">All Status</option>
                      <option value="new">New</option>
                      <option value="follow_up">Follow Up</option>
                      <option value="interested">Interested</option>
                    </select>
                  </div>

                  {/* From Date */}
                  <div className="d-flex flex-column">
                    <label htmlFor="fromDate" className="form-label mb-1">
                      From
                    </label>
                    <input
                      id="fromDate"
                      type="date"
                      className="form-control"
                      value={filters.from_date || ""}
                      onChange={(e) =>
                        handleFilterChange("from_date", e.target.value)
                      }
                    />
                  </div>

                  {/* To Date */}
                  <div className="d-flex flex-column">
                    <label htmlFor="toDate" className="form-label mb-1">
                      To
                    </label>
                    <input
                      id="toDate"
                      type="date"
                      className="form-control"
                      value={filters.to_date || ""}
                      onChange={(e) =>
                        handleFilterChange("to_date", e.target.value)
                      }
                    />
                  </div>

                  {/* Filter Button */}
                  <div className="d-flex align-items-center">
                    <button
                      className="btn btn-primary rounded-pill px-4 mt-2 mt-md-4"
                      style={{ height: "38px" }}
                      onClick={() => {
                        if (
                          filters.status ||
                          filters.from_date ||
                          filters.to_date
                        ) {
                          getEnquiries();
                        } else {
                          alert("Please select both From and To dates.");
                        }
                      }}
                    >
                      Filter
                    </button>
                  </div>
                </div>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>Full Name</th>
                        <th>Phone Number</th>
                        <th>Email</th>
                        <th>Address</th>
                        <th>Selected Property</th>
                        <th>Status</th>
                        <th>Message</th>
                        <th>Created At</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        // Show a loading state when data is being fetched
                        <tr>
                          <td colSpan="9" className="text-center">
                            <Loader />
                          </td>
                        </tr>
                      ) : notFoundMessage ? (
                        <tr>
                          <td colSpan="9" className="text-center text-danger">
                            {notFoundMessage}
                          </td>
                        </tr>
                      ) : enquiries.length === 0 ? (
                        // Show a "No Data Found" message when no data is available
                        <tr>
                          <td colSpan="9" className="text-center">
                            No Data Found
                          </td>
                        </tr>
                      ) : (
                        // Render the list of enquiries if data is available
                        enquiries.map((enquiry) => {
                          const formattedDate = new Date(
                            enquiry.created_at,
                          ).toLocaleDateString("en-GB", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          });

                          // Check if the message length is greater than 50 characters and truncate it
                          const truncatedMessage =
                            enquiry.message.length > 50
                              ? enquiry.message.slice(0, 50) + "..."
                              : enquiry.message;

                          return (
                            <tr key={enquiry.id}>
                              <td>{enquiry.full_name}</td>
                              <td>{enquiry.phone_number}</td>
                              <td>{enquiry.email}</td>
                              <td>{enquiry.address}</td>
                              <td>{enquiry.selected_property}</td>
                              <td>{enquiry.status}</td>
                              <td>{truncatedMessage}</td>
                              <td>{formattedDate}</td>
                              <td>
                                <button
                                  className="btn btn-icon btn-round btn-dark btn-sm me-2"
                                  onClick={() => openEditModal(enquiry.id)}
                                >
                                  <i className="fas fa-edit"></i>
                                </button>
                                <button
                                  className="btn btn-icon btn-round btn-danger btn-sm me-2"
                                  onClick={() => handleDelete(enquiry.id)}
                                >
                                  <i className="fas fa-trash"></i>
                                </button>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <Pagination totalPages={totalPages} setPage={setPage} page={page} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default Enquiries;
